import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  assignDateFeWheel,
  createDailyPageActivity,
  deleteDailyPageActivity,
  ensureDailyPageDate,
  getDailyPageDay,
  getDailyPageDayActivities,
  getDailyPageErrorMessage,
  getDailyPagePayload,
  getDaySections,
  isDailyPageApiSuccess,
  listWheelTemplates,
  patchDaySections,
  reorderChallenges,
  reorderOpinions,
  unassignDateFeWheel,
  updateDailyPageActivity,
} from '../../services/dailyPageApi';
import {
  getDailyMissionsPayload,
  isDailyMissionsApiSuccess,
  listDailyMissions,
} from '../../services/dailyMissionsApi';
import { parseDayList } from '../DailyMissions/dailyMissionUtils';
import MissionsGroupPanel from './MissionsGroupPanel';
import FeWheelPanel from './FeWheelPanel';
import ChallengesPanel from './ChallengesPanel';
import OpinionPanel from './OpinionPanel';
import FanOfTheDayPanel from './FanOfTheDayPanel';
import SectionsVisibilityPanel from './SectionsVisibilityPanel';
import {
  defaultSectionsVisible,
  formatIstDate,
  hiddenSectionKeys,
  isDayKey,
  normalizeSectionsVisible,
  splitActivities,
  statusColor,
} from './dailyTasksUtils';

function DailyTasksDayPage() {
  const { date } = useParams();
  const validDate = isDayKey(date);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expanded, setExpanded] = useState('sections');

  const [status, setStatus] = useState('scheduled');
  const [mission, setMission] = useState(null);
  const [feWheel, setFeWheel] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [challengeTypes, setChallengeTypes] = useState([]);
  const [opinions, setOpinions] = useState([]);
  const [fanOfTheDay, setFanOfTheDay] = useState(null);
  const [sectionsVisible, setSectionsVisible] = useState(() => defaultSectionsVisible());
  const [sectionsSaved, setSectionsSaved] = useState(() => defaultSectionsVisible());
  const sectionsDirty =
    JSON.stringify(sectionsVisible) !== JSON.stringify(sectionsSaved);

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    if (!validDate) return;
    setLoading(true);
    setError('');
    try {
      const ensureRes = await ensureDailyPageDate(date);
      if (!isDailyPageApiSuccess(ensureRes) && ensureRes?.http_status >= 400) {
        setError(getDailyPageErrorMessage(ensureRes, 'Failed to ensure day shell.'));
      }

      const [dayRes, activitiesRes, sectionsRes, templatesRes, missionsRes] = await Promise.all([
        getDailyPageDay(date),
        getDailyPageDayActivities(date),
        getDaySections(date),
        listWheelTemplates(),
        listDailyMissions({ startDate: date, endDate: date, limit: 5 }),
      ]);

      if (!isDailyPageApiSuccess(dayRes)) {
        setError(getDailyPageErrorMessage(dayRes, 'Failed to load day details.'));
      } else {
        const payload = getDailyPagePayload(dayRes) || {};
        setStatus(payload.status || 'scheduled');
        setMission(payload.missions || payload.mission || null);
        setFeWheel(payload.fe_wheel || null);
        setChallengeTypes(
          Array.isArray(payload.challenge_types) ? payload.challenge_types : []
        );
        setFanOfTheDay(payload.fan_of_the_day || null);

        let visibility = normalizeSectionsVisible(payload);
        if (isDailyPageApiSuccess(sectionsRes)) {
          visibility = normalizeSectionsVisible(getDailyPagePayload(sectionsRes) || {});
        }
        setSectionsVisible(visibility);
        setSectionsSaved(visibility);

        // Lists: activities CRUD is authoritative; aggregate is fallback
        if (isDailyPageApiSuccess(activitiesRes)) {
          const actPayload = getDailyPagePayload(activitiesRes) || {};
          const split = splitActivities(actPayload.activities || []);
          setChallenges(split.challenges);
          setOpinions(split.opinions);
        } else {
          setChallenges(
            Array.isArray(payload.daily_challenges)
              ? payload.daily_challenges
              : Array.isArray(payload.challenges)
                ? payload.challenges
                : []
          );
          setOpinions(
            Array.isArray(payload.opinion)
              ? payload.opinion
              : Array.isArray(payload.opinions)
                ? payload.opinions
                : []
          );
          if (activitiesRes?.http_status >= 400) {
            console.warn(
              getDailyPageErrorMessage(activitiesRes, 'Failed to load day activities.')
            );
          }
        }
      }

      if (isDailyPageApiSuccess(templatesRes)) {
        const tPayload = getDailyPagePayload(templatesRes) || {};
        const list = tPayload.templates || tPayload || [];
        setTemplates(Array.isArray(list) ? list : []);
      }

      if (isDailyMissionsApiSuccess(missionsRes)) {
        const parsed = parseDayList(getDailyMissionsPayload(missionsRes));
        const pack = parsed.days.find((d) => d.mission_date === date) || null;
        if (pack) setMission((prev) => prev || pack);
      }
    } catch (err) {
      setError(err.message || 'Failed to load day.');
    } finally {
      setLoading(false);
    }
  }, [date, validDate]);

  useEffect(() => {
    load();
  }, [load]);

  const wrap = async (fn, successMsg) => {
    setSaving(true);
    try {
      const res = await fn();
      if (!isDailyPageApiSuccess(res)) {
        notify(getDailyPageErrorMessage(res, 'Request failed.'), 'error');
        return false;
      }
      if (successMsg) notify(successMsg);
      await load();
      return true;
    } finally {
      setSaving(false);
    }
  };

  if (!validDate) {
    return (
      <Alert severity="error">
        Invalid date. Use an IST calendar day as <code>YYYY-MM-DD</code>.{' '}
        <Link to="/admin/daily-tasks">Back to schedule</Link>
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1.5,
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton component={Link} to="/admin/daily-tasks">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" sx={{ m: 0 }}>
                {formatIstDate(date)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                IST calendar day · {date}
              </Typography>
            </Box>
            <Chip size="small" label={status} color={statusColor(status)} />
          </Box>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={saving}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Activities CRUD: <code>activity_type</code> = challenge|opinion;{' '}
        <code>config.game_type</code> = game; <code>config.surface=daily_page</code> keeps them off
        Arcade. Section visibility toggles apply to the app immediately. Soft-published on save (no
        Phase 1 publish UI).
      </Alert>

      <Accordion
        expanded={expanded === 'sections'}
        onChange={(_, open) => setExpanded(open ? 'sections' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Section visibility{' '}
            <Chip
              size="small"
              label={
                hiddenSectionKeys(sectionsVisible).length
                  ? `${hiddenSectionKeys(sectionsVisible).length} hidden`
                  : 'all on'
              }
              color={hiddenSectionKeys(sectionsVisible).length ? 'warning' : 'success'}
              sx={{ ml: 1 }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionsVisibilityPanel
            sectionsVisible={sectionsVisible}
            onChange={setSectionsVisible}
            dirty={sectionsDirty}
            saving={saving}
            onSave={() =>
              wrap(
                () => patchDaySections(date, { sections_visible: sectionsVisible }),
                'Section visibility saved — app picks it up immediately.'
              )
            }
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'missions'}
        onChange={(_, open) => setExpanded(open ? 'missions' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Missions{' '}
            <Chip
              size="small"
              label={mission?.id ? `${mission.tasks?.length || 0} tasks` : 'missing'}
              color={mission?.id ? 'success' : 'default'}
              sx={{ ml: 1 }}
            />
            {sectionsVisible.missions === false && (
              <Chip size="small" color="warning" label="hidden" sx={{ ml: 0.5 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <MissionsGroupPanel date={date} mission={mission} />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'wheel'}
        onChange={(_, open) => setExpanded(open ? 'wheel' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            FE Wheel{' '}
            <Chip
              size="small"
              label={feWheel?.assigned || feWheel?.template_id ? 'assigned' : 'none'}
              color={feWheel?.assigned || feWheel?.template_id ? 'success' : 'default'}
              sx={{ ml: 1 }}
            />
            {sectionsVisible.fe_wheel === false && (
              <Chip size="small" color="warning" label="hidden" sx={{ ml: 0.5 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FeWheelPanel
            feWheel={feWheel}
            templates={templates}
            status={status}
            saving={saving}
            onAssign={(templateId) =>
              wrap(
                () => assignDateFeWheel(date, { template_id: templateId }),
                'Wheel template assigned (snapshot frozen).'
              )
            }
            onUnassign={() =>
              wrap(() => unassignDateFeWheel(date), 'Wheel unassigned for this date.')
            }
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'challenge'}
        onChange={(_, open) => setExpanded(open ? 'challenge' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Daily Challenge <Chip size="small" label={challenges.length} sx={{ ml: 1 }} />
            {sectionsVisible.daily_challenge === false && (
              <Chip size="small" color="warning" label="hidden" sx={{ ml: 0.5 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ChallengesPanel
            challenges={challenges}
            challengeTypes={challengeTypes}
            saving={saving}
            onCreate={(body) =>
              wrap(() => createDailyPageActivity(date, body), 'Challenge created.')
            }
            onUpdate={(id, body) =>
              wrap(() => updateDailyPageActivity(id, body), 'Challenge updated.')
            }
            onDelete={(id) =>
              wrap(() => deleteDailyPageActivity(id), 'Challenge deactivated.')
            }
            onReorder={(items) =>
              wrap(() => reorderChallenges(date, items), 'Challenge order saved.')
            }
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'opinion'}
        onChange={(_, open) => setExpanded(open ? 'opinion' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Opinion <Chip size="small" label={opinions.length} sx={{ ml: 1 }} />
            {sectionsVisible.opinion === false && (
              <Chip size="small" color="warning" label="hidden" sx={{ ml: 0.5 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OpinionPanel
            opinions={opinions}
            saving={saving}
            onCreate={(body) =>
              wrap(() => createDailyPageActivity(date, body), 'Opinion created.')
            }
            onUpdate={(id, body) =>
              wrap(() => updateDailyPageActivity(id, body), 'Opinion updated.')
            }
            onDelete={(id) =>
              wrap(() => deleteDailyPageActivity(id), 'Opinion deactivated.')
            }
            onReorder={(items) =>
              wrap(() => reorderOpinions(date, items), 'Opinion order saved.')
            }
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'fotd'}
        onChange={(_, open) => setExpanded(open ? 'fotd' : false)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Fan of the Day{' '}
            <Chip
              size="small"
              label={fanOfTheDay?.availability || '—'}
              sx={{ ml: 1 }}
            />
            {sectionsVisible.fan_of_the_day === false && (
              <Chip size="small" color="warning" label="hidden" sx={{ ml: 0.5 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FanOfTheDayPanel fanOfTheDay={fanOfTheDay} />
        </AccordionDetails>
      </Accordion>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DailyTasksDayPage;
